import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const ASSET = (name) => `/assets/tvs/${name}`;

const HORSE_URL = ASSET("corp-tvs-logo.png");
const GENUINE_URL = ASSET("Genuine.png");
const LIKE_URL = ASSET("Like.png");
const DISLIKE_URL = ASSET("DisLike.png");

// Intentionally non-existent path so the browser renders the native broken-image icon
// (matching the reference screenshot, where the per-SKU part image is missing).
const PART_IMG_URL = ASSET("part-missing.png");

export default function Verify() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/verify/${token}`);
        const expireLink = data?.qr?.expire_page_link;
        if (data?.found && data?.limit_reached && expireLink) {
          window.location.replace(expireLink);
          return;
        }
        setData(data);
      } catch {
        setData({ found: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submitFeedback = async (feedbackType) => {
    if (!/^[0-9]{10}$/.test(mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/verify/${token}/confirm`, {
        mobile_number: mobile,
        feedback_type: feedbackType,
      });
      setSubmitted(true);
      toast.success("Thanks for your feedback!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#EEEEEE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          aria-label="Loading"
          style={{
            width: 48,
            height: 48,
            border: "4px solid rgba(0,0,0,0.12)",
            borderTopColor: "#475569",
            borderRadius: "50%",
            animation: "tvs-verify-spin 0.9s linear infinite",
          }}
        />
        <style>{`@keyframes tvs-verify-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!data?.found) {
    return (
      <Page>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <span style={{ fontSize: "18pt", fontWeight: "bold", color: "#c62828" }}>
            Invalid QR Code · Please Contact Customer Care
          </span>
        </div>
      </Page>
    );
  }

  if (data.limit_reached) {
    return (
      <Page>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <span style={{ fontSize: "18pt", fontWeight: "bold", color: "#0F172A" }}>
            Genuine Check is already done.
          </span>
          <div style={{ marginTop: 8, fontSize: "13pt", color: "#475569" }}>
            Please Contact the Customer Care.
          </div>
        </div>
      </Page>
    );
  }

  const qr = data.qr;
  const partDesc = `${qr.part_number || ""} - ${qr.part_name || ""}`.trim();

  return (
    <Page>
      <div style={{ paddingTop: 40, textAlign: "center" }}>
        <span
          id="lblResult"
          style={{
            display: "inline-block",
            fontSize: "18pt",
            fontWeight: "bold",
            color: "#000",
          }}
        >
          Thank you for buying TVSM Genuine part
        </span>
      </div>

      <div style={{ marginTop: 32, display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 12 }}>
        <img
          id="Image1"
          src={GENUINE_URL}
          alt="Genuine"
          style={{ width: 380, display: "inline-block" }}
        />
        <img
          id="Image2"
          src={PART_IMG_URL}
          alt=" "
          style={{ display: "inline-block", width: 30, height: 40 }}
        />
      </div>

      <div style={{ marginTop: 16, textAlign: "center" }}>
        <span
          id="lblPartDesc"
          style={{
            display: "inline-block",
            fontSize: "18pt",
            fontWeight: "bold",
            color: "#000",
          }}
        >
          {partDesc}
        </span>
      </div>

      <div style={{ marginTop: 56, display: "flex", justifyContent: "center" }}>
        {!submitted ? (
          <div
            style={{
              border: "1px solid #6FA8DC",
              padding: "10px 24px 18px",
              background: "transparent",
              textAlign: "center",
              minWidth: 360,
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <span id="lblFeedBack" style={{ fontSize: "10pt", color: "#000" }}>
                Do You Like This Page:
              </span>
            </div>
            <table
              className="tBorder"
              style={{ margin: "0 auto", borderCollapse: "collapse" }}
            >
              <tbody>
                <tr align="center">
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    <span id="lblMobNo" style={{ marginRight: 8, fontSize: "10pt" }}>
                      Mobile No
                    </span>
                    <input
                      name="txtMobNumber"
                      id="txtMobNumber"
                      type="text"
                      inputMode="numeric"
                      maxLength={12}
                      className="TextBoxStyle"
                      value={mobile}
                      onChange={(e) =>
                        setMobile(e.target.value.replace(/[^0-9]/g, ""))
                      }
                      style={{
                        padding: "2px 4px",
                        fontSize: 13,
                        border: "1px solid #888",
                        width: 170,
                        verticalAlign: "middle",
                      }}
                    />
                  </td>
                  <td style={{ padding: "6px 4px" }}>
                    <input
                      type="image"
                      name="btnLike"
                      id="btnLike"
                      src={LIKE_URL}
                      alt="Like"
                      disabled={submitting}
                      onClick={(e) => {
                        e.preventDefault();
                        submitFeedback("like");
                      }}
                      style={{ height: 74, width: 60, cursor: "pointer", verticalAlign: "middle" }}
                    />
                    <input
                      type="image"
                      name="btnDisLike"
                      id="btnDisLike"
                      src={DISLIKE_URL}
                      alt="Dislike"
                      disabled={submitting}
                      onClick={(e) => {
                        e.preventDefault();
                        submitFeedback("dislike");
                      }}
                      style={{ height: 82, width: 60, cursor: "pointer", marginLeft: 4, verticalAlign: "middle" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              fontSize: "12pt",
              fontWeight: 600,
              color: "#16a34a",
              padding: "16px 24px",
              border: "1px solid #6FA8DC",
            }}
          >
            Thank you! Your feedback has been recorded.
          </div>
        )}
      </div>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#EFEDEC",
        color: "#000",
        fontFamily:
          'Arial, "Helvetica Neue", Helvetica, sans-serif',
      }}
    >
      {/* Dark blue header band */}
      <div
        style={{
          background: "#1B3F8F",
          height: 90,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              color: "#E12A1B",
              fontWeight: 800,
              fontSize: 30,
              letterSpacing: 1,
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1,
            }}
          >
            TVS
          </span>
          <img
            src={HORSE_URL}
            alt="TVS"
            style={{ height: 34, width: "auto", display: "block" }}
          />
        </div>
      </div>

      {/* Orange strip */}
      <div style={{ background: "#F06A1A", height: 12 }} />

      {/* Body content */}
      <div>{children}</div>
    </div>
  );
}
